/**
 * The Checks tab of a borrower's quarter (DOP-626, after deal screening's).
 *
 * Every figure the package did not have is a check, and the quarter cannot be
 * locked while one is open. They come one at a time — a numbered list on the
 * left, the check on screen on the right — so an analyst works through them
 * rather than scanning a stack of cards:
 *  - "I know the figure": a value and where it came from. The source is
 *    required; the workbook prints the figure as entered by the person.
 *  - "Accept as not available": the workbook shows n/a and tests that use it
 *    read as unknown.
 * Beside it, where the run looked: its reason, the package's documents, and
 * last period's value when there is one. Previous / Skip to next sit at the
 * bottom (the user's call on deal screening), and a save moves to the next
 * open check by itself.
 *
 * Answers already given are listed under "Answered", with who and when.
 */

import { ChevronLeft, ChevronRight, CircleCheck, Search } from "lucide-react";
import React, { useId, useState } from "react";

import type {
	MonitoringFigure,
	MonitoringRun,
	MonitoringSetFigurePayload,
} from "../../types/monitoring";
import { formatEpochMs, formatFigure } from "./runFormat";

export interface GapsListProps {
	run: MonitoringRun;
	/** Resolves when the write and the refetch that follows it are done. */
	onSetFigure: (figureKey: string, payload: MonitoringSetFigurePayload) => Promise<void>;
}

/** The exact phrase the server stores for an accepted-as-missing figure. */
export const ACCEPTED_NOT_AVAILABLE_NOTE = "Accepted as not available";

/** What the number means, in the unit the ledger stores. */
const UNIT_HINT: Record<MonitoringFigure["unit"], string> = {
	usd_m: "USD millions, e.g. 130.0",
	x: "a multiple, e.g. 4.85",
	pct: "a fraction, e.g. 0.083 for 8.3%",
};

const BUTTON =
	"inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-[10px] border px-4 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782] disabled:cursor-not-allowed disabled:opacity-50";
const SECONDARY = `${BUTTON} border-gray-200 bg-white text-[#1F2937] hover:bg-gray-50`;
const PRIMARY = `${BUTTON} border-[#004C4B] bg-[#004C4B] text-white hover:bg-[#003a3a]`;
const INPUT =
	"h-10 w-full rounded-lg border border-[#d1d5db] bg-white px-3 text-sm text-[#111111] focus:border-[#004C4B] focus:outline-none focus:ring-1 focus:ring-[#004C4B]";

interface CheckProps {
	figure: MonitoringFigure;
	run: MonitoringRun;
	saving: boolean;
	disabled: boolean;
	onSave: (payload: MonitoringSetFigurePayload) => void;
}

/** One check: the question, the two answers, and where the run looked. */
const Check: React.FC<CheckProps> = ({ figure, run, saving, disabled, onSave }) => {
	const [mode, setMode] = useState<"answer" | "accept">("answer");
	const [value, setValue] = useState("");
	const [note, setNote] = useState("");
	const valueId = useId();
	const noteId = useId();
	const choice = useId();
	const parsed = Number(value);
	const answering = mode === "answer";
	const ready =
		!saving && !disabled && (!answering || (value.trim() !== "" && Number.isFinite(parsed) && note.trim() !== ""));
	const card = (selected: boolean) =>
		`rounded-xl bg-white ${selected ? "border-2 border-[#004C4B] px-[15px] py-[13px]" : "border border-gray-200 px-4 py-3.5"}`;

	return (
		<div className="flex flex-col gap-6 lg:flex-row lg:items-start">
			<form
				aria-label={`Check: ${figure.label}`}
				onSubmit={(event) => {
					event.preventDefault();
					if (!ready) return;
					onSave(
						answering
							? { value: parsed, note: note.trim() }
							: { note: ACCEPTED_NOT_AVAILABLE_NOTE, accept_not_available: true },
					);
				}}
				className="flex min-w-0 flex-1 flex-col gap-4 lg:max-w-[560px]"
			>
				<div>
					<p className="text-xs font-bold uppercase tracking-[0.06em] text-[#b54708]">Not in the package</p>
					<h2 className="mt-1 text-[22px] font-semibold leading-tight text-[#111111]">
						What was {run.period.label} {figure.label.charAt(0).toLowerCase() + figure.label.slice(1)}?
					</h2>
					<p className="mt-1 text-sm text-[#5B6472]">
						Doppler read the {run.package.length} {run.package.length === 1 ? "document" : "documents"} in
						this package and didn't find it.
					</p>
				</div>
				<div role="radiogroup" aria-label="What to do" className="flex flex-col gap-2.5">
					<div className={card(answering)}>
						<label className="flex cursor-pointer gap-3">
							<input
								type="radio"
								name={choice}
								checked={answering}
								onChange={() => setMode("answer")}
								disabled={disabled}
								className="mt-0.5 h-[18px] w-[18px] shrink-0 accent-[#004C4B]"
							/>
							<span>
								<span className="block text-sm font-semibold text-[#111111]">I know the figure</span>
								<span className="mt-0.5 block text-[12.5px] text-[#5B6472]">
									It goes in the workbook marked as entered by you, with where it came from.
								</span>
							</span>
						</label>
						{answering && (
							<div className="mt-3.5 grid gap-3 pl-[30px] sm:grid-cols-[minmax(0,180px)_minmax(0,1fr)]">
								<div className="flex flex-col gap-1.5">
									<label htmlFor={valueId} className="text-[12.5px] font-semibold text-[#3f4247]">
										{figure.label}
									</label>
									<input
										id={valueId}
										value={value}
										onChange={(event) => setValue(event.target.value)}
										inputMode="decimal"
										disabled={disabled}
										className={INPUT}
									/>
									<span className="text-xs text-[#6B7280]">{UNIT_HINT[figure.unit]}</span>
								</div>
								<div className="flex flex-col gap-1.5">
									<label htmlFor={noteId} className="text-[12.5px] font-semibold text-[#3f4247]">
										Where it came from
									</label>
									<input
										id={noteId}
										value={note}
										onChange={(event) => setNote(event.target.value)}
										placeholder="e.g. Borrower email, Feb 12 — Q4 capex"
										disabled={disabled}
										className={INPUT}
									/>
								</div>
							</div>
						)}
					</div>
					<div className={card(!answering)}>
						<label className="flex cursor-pointer gap-3">
							<input
								type="radio"
								name={choice}
								checked={!answering}
								onChange={() => setMode("accept")}
								disabled={disabled}
								className="mt-0.5 h-[18px] w-[18px] shrink-0 accent-[#004C4B]"
							/>
							<span>
								<span className="block text-sm font-semibold text-[#111111]">Accept as not available</span>
								<span className="mt-0.5 block text-[12.5px] text-[#5B6472]">
									The workbook shows n/a. Tests that use it read as unknown.
								</span>
							</span>
						</label>
					</div>
				</div>
				<div className="flex justify-end">
					<button type="submit" disabled={!ready} className={PRIMARY}>
						{saving ? "Saving…" : answering ? "Save and next" : "Accept and next"}
					</button>
				</div>
			</form>

			<aside
				aria-label="Where Doppler looked"
				className="w-full self-start rounded-[14px] border border-gray-200 bg-white px-4 py-3.5 lg:w-[320px] lg:shrink-0"
			>
				<p className="flex items-center gap-2 text-sm font-semibold text-[#111111]">
					<Search className="h-4 w-4 text-[#004C4B]" aria-hidden />
					Where Doppler looked
				</p>
				{figure.gap_reason && <p className="mt-1.5 text-[13px] text-[#3f4247]">{figure.gap_reason}</p>}
				{run.package.length > 0 && (
					<ul className="mt-2">
						{run.package.map((doc) => (
							<li
								key={doc.file_id}
								className="flex gap-2.5 border-b border-[#f1f2f4] py-2 text-[13px] last:border-0"
							>
								<span className="min-w-0 flex-1 truncate font-medium text-[#1F2937]">{doc.filename}</span>
								<span className="shrink-0 text-[#6B7280]">{doc.doc_type.replace(/_/g, " ")}</span>
							</li>
						))}
					</ul>
				)}
				{figure.prior && figure.prior.value !== null && (
					<p className="mt-2 rounded-lg bg-[#f7f7f6] px-3 py-2 text-[12.5px] text-[#3f4247]">
						Last period's figure was {formatFigure(figure.prior.value, figure.unit)}.
					</p>
				)}
			</aside>
		</div>
	);
};

const GapsList: React.FC<GapsListProps> = ({ run, onSetFigure }) => {
	const [chosenKey, setChosenKey] = useState<string | null>(null);
	const [busyKey, setBusyKey] = useState<string | null>(null);

	const locked = run.status === "locked";
	const gaps = run.figures.filter((figure) => figure.state === "gap").sort((a, b) => a.sort_order - b.sort_order);
	const resolved = run.figures
		.filter((figure) => figure.state === "manual" || figure.state === "not_available")
		.sort((a, b) => a.sort_order - b.sort_order);
	// A saved check leaves the list; the next open one takes its place.
	const current = gaps.find((figure) => figure.key === chosenKey) ?? gaps[0] ?? null;
	const position = current ? gaps.indexOf(current) : -1;

	const save = async (figure: MonitoringFigure, payload: MonitoringSetFigurePayload) => {
		setBusyKey(figure.key);
		try {
			await onSetFigure(figure.key, payload);
			setChosenKey(null);
		} catch {
			// Not saved (the page already said why): stay on this check with
			// what was typed rather than moving on as if it had been.
			setChosenKey(figure.key);
		} finally {
			setBusyKey(null);
		}
	};

	return (
		<div className="mx-auto flex max-w-[1280px] flex-col gap-6">
			{locked && (
				<p className="text-sm text-[#5B6472]">
					This period is locked. Its answers are part of the record and can't be changed.
				</p>
			)}

			{gaps.length === 0 ? (
				<div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-5 py-5 text-sm text-[#3f4247]">
					<CircleCheck className="h-5 w-5 shrink-0 text-[#067647]" aria-hidden />
					No open checks. Every figure is in the package or answered.
				</div>
			) : (
				<div className="grid items-start gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
					<nav aria-label="Checks" className="rounded-[14px] border border-gray-200 bg-white p-3">
						<div className="px-2 pb-3 pt-1">
							<p className="text-sm font-semibold text-[#111111]">
								{gaps.length === 1 ? "1 check needs you" : `${gaps.length} checks need you`}
							</p>
							<p className="mt-0.5 text-[12.5px] text-[#6B7280]">Work through them in order, or click any one.</p>
						</div>
						<ol className="flex flex-col gap-0.5">
							{gaps.map((figure, index) => {
								const selected = figure.key === current?.key;
								return (
									<li key={figure.key}>
										<button
											type="button"
											data-testid={`check-row-${figure.key}`}
											aria-current={selected ? "true" : undefined}
											onClick={() => setChosenKey(figure.key)}
											className={`flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left ${
												selected ? "bg-[#E6F3F3]" : "hover:bg-[#f7f7f6]"
											}`}
										>
											<span
												className={`mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
													selected ? "bg-[#004C4B] text-white" : "border border-[#d1d5db] text-[#5B6472]"
												}`}
												aria-hidden
											>
												{index + 1}
											</span>
											<span className="flex min-w-0 flex-col gap-0.5">
												<span
													className={`truncate text-[13.5px] ${
														selected ? "font-semibold text-[#004C4B]" : "font-medium text-[#1F2937]"
													}`}
												>
													{figure.label}
												</span>
												<span className="text-xs text-[#b54708]">Not in the package</span>
											</span>
										</button>
									</li>
								);
							})}
						</ol>
					</nav>
					{current && (
						<div className="min-w-0">
							<Check
								key={current.key}
								figure={current}
								run={run}
								saving={busyKey === current.key}
								disabled={locked || busyKey !== null}
								onSave={(payload) => void save(current, payload)}
							/>
							<div className="mt-5 flex flex-wrap items-center gap-3 border-t border-gray-200 pt-4" data-testid="check-footer">
								<button
									type="button"
									className={SECONDARY}
									disabled={position <= 0}
									onClick={() => setChosenKey(gaps[position - 1].key)}
								>
									<ChevronLeft className="h-4 w-4" aria-hidden />
									Previous
								</button>
								<p className="flex-1 text-center text-[12.5px] text-[#6B7280]">
									<span className="font-semibold text-[#3f4247]">
										Check {position + 1} of {gaps.length}
									</span>
									{" · "}the quarter can be locked once every check is answered
								</p>
								<button
									type="button"
									className={SECONDARY}
									disabled={position >= gaps.length - 1}
									onClick={() => setChosenKey(gaps[position + 1].key)}
								>
									Skip to next
									<ChevronRight className="h-4 w-4" aria-hidden />
								</button>
							</div>
						</div>
					)}
				</div>
			)}

			{resolved.length > 0 && (
				<section aria-label="Answered" className="rounded-xl border border-gray-200 bg-white">
					<h3 className="border-b border-[#eef0f2] px-5 py-3 text-sm font-semibold text-[#111111]">
						Answered <span className="font-normal text-[#6B7280]">{resolved.length}</span>
					</h3>
					<ul>
						{resolved.map((figure) => (
							<li key={figure.key} className="border-b border-[#f1f2f4] px-5 py-3 last:border-0">
								<p className="text-sm text-[#111111]">
									<span className="font-semibold">{figure.label}</span>
									{figure.state === "manual" && (
										<span className="ml-2 tabular-nums">{formatFigure(figure.value, figure.unit)}</span>
									)}
									{figure.state === "not_available" && <span className="ml-2 text-[#6B7280]">n/a</span>}
								</p>
								<p className="mt-0.5 text-xs text-[#6B7280]">
									{figure.note ?? "—"}
									{figure.entered_by_name ? ` · ${figure.entered_by_name}` : ""}
									{figure.entered_at ? ` · ${formatEpochMs(figure.entered_at)}` : ""}
								</p>
							</li>
						))}
					</ul>
				</section>
			)}
		</div>
	);
};

export default GapsList;
